import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App error boundary caught:', error, errorInfo);
  }

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isDev = import.meta.env.DEV;
    const message = this.state.error?.message || 'An unexpected error occurred.';

    return (
      <div className="min-h-screen bg-dark-base flex items-center justify-center p-6">
        <div
          className="w-full max-w-md rounded-xl border border-dark-border bg-dark-surface p-8 text-center shadow-lg"
          role="alert"
        >
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-900/30">
            <AlertTriangle className="h-6 w-6 text-red-400" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-slate-100">Something went wrong</h1>
          <p className="mt-2 text-sm text-slate-400">
            The application hit an unexpected error. You can try again or reload the page.
          </p>
          {isDev && message ? (
            <pre className="mt-4 max-h-32 overflow-auto rounded-lg border border-dark-border bg-dark-base p-3 text-left text-xs text-red-300 whitespace-pre-wrap">
              {message}
            </pre>
          ) : null}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button type="button" className="btn-secondary-mdl" onClick={this.handleTryAgain}>
              Try again
            </button>
            <button
              type="button"
              className="btn-primary-mdl inline-flex items-center gap-2"
              onClick={this.handleReload}
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
